import React, { useEffect, useState } from "react";

import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { useTournamentAuth } from "../../utils/authorization";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import SettingsTab from "../../components/AdminDash/Tabs/SettingsTab";
import TournamentSettingsForm from "../../components/Tournament/TournamentSettingsForm";
import { TournamentPageSkeleton } from "../../components/UI/DesignStandards";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

export default function TournamentSettings() {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const { loading: authLoading } = useAuth();
  const { canManage, loading: permissionLoading } =
    useTournamentAuth(tournamentId);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("tournaments")
          .select("*")
          .eq("id", tournamentId)
          .single();
        if (error) throw error;
        setTournament(data);
      } catch (error) {
        console.error("Error fetching tournament:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [tournamentId]);

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Tournaments", path: "/tournaments" },
    {
      label: tournament?.name || "Tournament",
      path: tournamentPath(tournamentSlug),
    },
    { label: "Settings" },
  ];

  if (loading || authLoading || permissionLoading) {
    return (
      <TournamentPageSkeleton
        title="Settings"
        subtitle="Name, dates, QR codes, and deleting the tournament."
        cards={3}
      />
    );
  }

  if (!canManage) {
    return (
      <div className="card-board p-8 text-center">
        <h1 className="heading-display text-2xl mb-2">Access Denied</h1>
        <p className="text-brand-muted">
          You don't have permission to access this page.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="space-y-6">
        <div className="card-board overflow-hidden">
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Settings
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              Name, dates, QR codes, and deleting the tournament.
            </p>
          </div>
          <div className="p-6">
            <TournamentSettingsForm
              tournament={tournament}
              onSaved={(updates) =>
                setTournament((prev) => ({ ...prev, ...updates }))
              }
              framed={false}
            />
          </div>
        </div>

        <SettingsTab tournament={tournament} />
      </div>
    </>
  );
}
